import React, { useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import useTheme from '@material-ui/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Tooltip from '@material-ui/core/Tooltip';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Link from '@material-ui/core/Link';
import { HiOutlineSearch, HiOutlineRefresh, HiOutlineFilter } from 'react-icons/hi';

const hasActiveFilterValue = (node) => {
    if (!node) return false;
    if (Array.isArray(node)) return node.some(hasActiveFilterValue);
    if (!React.isValidElement(node)) return false;
    const { props } = node;
    if (!props) return false;
    if ('value' in props) {
        const val = props.value;
        if (val !== null && val !== undefined && val !== '' && val !== -1 && !(Array.isArray(val) && val.length === 0)) {
            return true;
        }
        return false;
    }
    if ('checked' in props && props.checked === true) {
        return true;
    }
    if (props.control && hasActiveFilterValue(props.control)) {
        return true;
    }
    if (props.children) {
        return React.Children.toArray(props.children).some(hasActiveFilterValue);
    }
    return false;
};

// actionsInFilterPanel (report pages): on mobile the actions move out of the header row into the
// filter dropdown as normal labelled buttons, below the filters. Desktop is unchanged.
// filtersBelow (large filter groups): on desktop the filters get their own row under the header row
// instead of sitting inline with the title. On mobile the dropdown stays mounted while closed, so
// filters that keep their own state (e.g. the batch selector) don't lose the selection.
// panelExtra (mobile only): extra content at the bottom of the filter dropdown, full width (e.g. the
// Daily Entries upload / manual entry boxes). The dropdown stays mounted for it as well.
const PageHeader = ({ title, subtitle, breadcrumbs, actions, filters, filterApplied, onSearch, onClearSearch, actionsInFilterPanel, filtersBelow, panelExtra }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });
    const [showFilters, setShowFilters] = useState(false);
    const [searchApplied, setSearchApplied] = useState(false);
    const hasActions = actions && actions.length > 0;
    const actionsInPanel = !isDesktop && actionsInFilterPanel && filters;

    const isFilterActive = filterApplied !== undefined
        ? Boolean(filterApplied)
        : actionsInFilterPanel
            ? searchApplied
            : (searchApplied || hasActiveFilterValue(filters));

    const handleSearch = (e) => {
        setSearchApplied(true);
        if (onSearch) onSearch(e);
    };

    const handleClearSearch = (e) => {
        setSearchApplied(false);
        if (onClearSearch) onClearSearch(e);
    };

    const renderButton = (action, index) => (
        <Button
            key={index}
            variant={action.variant || 'contained'}
            color={action.color || 'primary'}
            onClick={(e) => {
                if (actionsInPanel && (action.variant || 'contained') === 'contained') {
                    setSearchApplied(true);
                }
                if (action.onClick) action.onClick(e);
            }}
            href={action.href}
            download={action.download}
            disabled={action.disabled}
            endIcon={action.icon}
            size="small"
        >
            {action.label}
        </Button>
    );

    return (
        <div className="pp-page-header-wrap">
            <div className="page-header" style={{
                display: 'flex',
                alignItems: 'center',
                gap: isDesktop ? '12px' : '8px',
                flexWrap: 'nowrap',
            }}>
                <div style={isDesktop ? { marginRight: 'auto', flexShrink: 0 } : { marginRight: 'auto', flexShrink: 1, minWidth: 0 }}>
                    {breadcrumbs && breadcrumbs.length > 0 && (
                        <Breadcrumbs aria-label="breadcrumb" className="pp-page-header-breadcrumbs">
                            {breadcrumbs.map((crumb, index) =>
                                crumb.onClick ? (
                                    <Link key={index} color="inherit" href="#" onClick={(e) => { e.preventDefault(); crumb.onClick(); }}>
                                        {crumb.label}
                                    </Link>
                                ) : (
                                    <Typography key={index} color="textPrimary">{crumb.label}</Typography>
                                )
                            )}
                        </Breadcrumbs>
                    )}
                    <Typography display="block" variant="h3" style={isDesktop ? { whiteSpace: 'nowrap' } : {
                        // mobile: shrink with the screen width and wrap to at most 2 lines so the icons stay on screen
                        fontSize: 'clamp(15px, 4.5vw, 20px)',
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography display="block" variant="body2" color="textSecondary">
                            {subtitle}
                        </Typography>
                    )}
                </div>
                {isDesktop && !filtersBelow && filters}
                {isDesktop && (onSearch || onClearSearch) && (
                    <div className="flex-gap-6" style={{ flexShrink: 0 }}>
                        {onSearch && (
                            <Tooltip title="Search">
                                <IconButton size="small" onClick={handleSearch} className="pp-icon-btn-contained-primary">
                                    <HiOutlineSearch size={20} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {onClearSearch && (
                            <Tooltip title="Reset">
                                <IconButton size="small" onClick={handleClearSearch} className="pp-icon-btn-outlined">
                                    <HiOutlineRefresh size={20} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                )}
                {!isDesktop && filters && (
                    <Tooltip title="Filter">
                        <IconButton
                            size="small"
                            onClick={() => setShowFilters(!showFilters)}
                            className={showFilters ? 'pp-icon-btn-contained-secondary' : 'pp-icon-btn-outlined'}
                        >
                            <HiOutlineFilter size={20} />
                            {isFilterActive && <span className="pp-filter-status-dot" />}
                        </IconButton>
                    </Tooltip>
                )}
                {hasActions && !actionsInPanel && (
                    <div className="flex-gap-6" style={{ flexShrink: 0 }}>
                        {actions.map((action, index) =>
                            isDesktop ? renderButton(action, index) : (
                                <Tooltip key={index} title={action.label}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={action.onClick}
                                            href={action.href}
                                            download={action.download}
                                            disabled={action.disabled}
                                            color={action.color || 'primary'}
                                            className={(action.variant || 'contained') === 'contained'
                                                ? `pp-icon-btn-contained-${action.color || 'primary'}`
                                                : 'pp-icon-btn-outlined'}
                                        >
                                            {action.icon}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )
                        )}
                    </div>
                )}
            </div>
            {isDesktop && filtersBelow && filters && (
                <div className="table-filter-container pp-filter-row-desktop">
                    {filters}
                </div>
            )}
            {!isDesktop && (showFilters || filtersBelow || panelExtra) && filters && (
                <div className={showFilters ? undefined : 'd-none'}>
                <div className="table-filter-container pp-mobile-filter-row">
                    {filters}
                    {(onSearch || onClearSearch) && (
                        <div className="flex-gap-6 pp-mobile-filter-actions">
                            {onSearch && (
                                <Tooltip title="Search">
                                    <IconButton size="small" onClick={handleSearch} className="pp-icon-btn-contained-primary">
                                        <HiOutlineSearch size={20} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {onClearSearch && (
                                <Tooltip title="Reset">
                                    <IconButton size="small" onClick={handleClearSearch} className="pp-icon-btn-outlined">
                                        <HiOutlineRefresh size={20} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    )}
                    {actionsInPanel && hasActions && (
                        <div className="pp-mobile-filter-buttons">
                            {actions.map(renderButton)}
                        </div>
                    )}
                    {panelExtra && (
                        <div className="pp-mobile-filter-buttons">
                            {panelExtra}
                        </div>
                    )}
                </div>
                </div>
            )}
        </div>
    );
};

export default PageHeader;
